import {
  useActiveLab,
  useDispatch,
  useKeypointsFromOrderId,
  useLabUsers,
  useNotesFromOrderId,
} from '@witmetrics/data-store';
import { isEmpty } from '@witmetrics/utils/misc';
import { mapToKey } from '@witmetrics/utils/arrays';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useLoadedFiles } from '@/providers/LoadedFilesProvider';
import NotesField, {
  type NotesFieldProps,
} from '@/components/inputs/NotesField';
import NotesTimeline from '@/components/NotesTimeline';
import Section from '../../Section';
import SectionTitle from '../../SectionTitle';
import TimelineMenu from './TimelineMenu';
import TimelineToggle from './TimelineToggle';
import { classes, createOrderNote, useTimelineSettings } from './utils';

export default function TimelineSection() {
  const dispatch = useDispatch();
  const { onApiError, activeOrderId } = useAppState();
  const activeLab = useActiveLab();
  const keypoints = useKeypointsFromOrderId(activeOrderId);
  const notes = useNotesFromOrderId(activeOrderId);
  const labUsers = useLabUsers();
  const { addLoadedFiles } = useLoadedFiles();
  const [timelineSettings, setTimelineSettings] = useTimelineSettings();
  const [isNewNoteShown, toggleNewNote] = useToggle();
  const [isSavingNote, toggleSavingNote] = useToggle();

  const handleSaveNote: NotesFieldProps['onSave'] = async (
    message,
    selectedFiles
  ) => {
    try {
      toggleSavingNote(true);
      const { orderNote, attachments } = await createOrderNote(
        activeOrderId!,
        message,
        selectedFiles
      );
      addLoadedFiles(
        attachments.reduce(
          (a, b) => ({
            ...a,
            [b.noteFile.fileId]: b.selectedFile.source,
            [`preview-${b.noteFile.file.versions[0].id}`]:
              b.selectedFile.previewSource,
          }),
          {}
        )
      );
      dispatch.addOrderNotes([orderNote]);
      dispatch.addNoteFiles(mapToKey(attachments, 'noteFile'));
      toggleSavingNote(false);
      toggleNewNote(false);
    } catch (err) {
      toggleSavingNote(false);
      onApiError(err, 'Error saving note');
    }
  };

  const handleNewNote = () => {
    toggleNewNote(true);
  };

  const renderContent = () => {
    // if (isEmpty([...notes, ...fileNotes, ...keypoints])) {
    if (isEmpty([...notes, ...keypoints])) {
      return (
        <div className={classes.message}>
          No notes or keypoints for current order.
        </div>
      );
    }

    return (
      <NotesTimeline
        editable={!!activeLab}
        timelineSettings={timelineSettings || 'ALL'}
        keypoints={keypoints}
        notes={notes}
        // fileNotes={fileNotes}
        fileNotes={[]}
      />
    );
  };

  if (!activeOrderId) return null;

  return (
    <Section className={classes.section}>
      <SectionTitle
        title="Timeline"
        info="Notes are visible to both the customer & the lab."
        renderControls={() => (
          <>
            <TimelineToggle
              className={classes.toggle}
              optionClassName={classes.toggleOption}
              value={timelineSettings!}
              onChange={setTimelineSettings}
            />
            <TimelineMenu onAddNote={handleNewNote} />
          </>
        )}
      />
      {isNewNoteShown && (
        <NotesField
          allowAttachments
          isSaving={isSavingNote}
          placeholder="Add a note, @mention a colleague..."
          users={mapToKey(labUsers, 'user')}
          onSave={handleSaveNote}
          onCancel={() => toggleNewNote(false)}
        />
      )}
      {renderContent()}
    </Section>
  );
}
